import * as React from 'react';
import {useRef, useContext} from 'react';
import {assignProps, createView} from '@sail/engine';
import {search, cancelCircleFilled} from '@sail/icons/react/Icon';
import type {View} from '@sail/engine';
import type {ListCollection} from '@sail/react-aria';
import {
  FocusScope,
  useComboBox,
  useListCollection,
  useMultiComboBoxState,
} from '@sail/react-aria';
import {useFormFieldProps} from './FormField';
import {Icon} from './Icon';
import {CompositeField, CompositeFieldConfig} from './CompositeField';
import {Popover, DeprecatedPopoverContext} from './popover';
import type {DismissValue} from './popover';
import {Action} from './Action';
import type {ListBoxProps} from './ListBox';
import {TextField, TextFieldConfig} from './TextField';
import {ListBox, ListBoxContext} from './ListBox';
import {ListBoxGroupTransforms} from './ListBoxGroup';
import type {ListBoxGroupProps} from './ListBoxGroup';
import {ListBoxItemTransforms} from './ListBoxItem';
import type {ListBoxItemProps} from './ListBoxItem';
import type {SearchFieldOptions as MultiSearchFieldOptions} from './DO_NOT_USE_SearchField';
import {
  SearchField as MultiSearchField,
  SearchFieldContext as MultiSearchFieldContext,
} from './DO_NOT_USE_SearchField';

import {css} from '../css';
import {useUiConfig} from '../providers/UiConfigProvider';
import type {SearchFieldProps} from './SearchFieldConfig';
import {
  type SearchFieldProps as MultiSearchFieldProps,
  SearchFieldConfig,
} from './DO_NOT_USE_SearchFieldConfig';

const DISMISS_ON_ESCAPE: DismissValue = {
  outsidePress: false,
  outsideFocus: false,
  escapeKey: true,
};

// TODO(craigcav): should probably use something l10n friendly here like `useFilter`.
function contains<T>(
  textValue: string,
  inputValue: string,
  _userItem: T,
): boolean {
  return textValue.toLowerCase().includes(inputValue.toLowerCase());
}

function useSearchFieldState<T extends object>({
  children,
  data,
  item,
  group,
  ...props
}: SearchFieldProps<T>) {
  const {collection: inheritedCollection, options} = useContext(
    MultiSearchFieldContext,
  );

  // Track whether we're using inherited state or not so we can't violate the
  // rules of hooks by switching between the two.
  const isUsingInheritedCollection = useRef(Boolean(inheritedCollection));

  let collection: ListCollection<ListBoxItemProps, ListBoxGroupProps>;
  if (isUsingInheritedCollection.current) {
    if (inheritedCollection == null) {
      throw new Error(
        'Cannot switch from overriding the collection in SearchField to not overriding',
      );
    }
    collection = inheritedCollection;
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    collection = useListCollection({
      children,
      data,
      item,
      group,
      itemTransforms: ListBoxItemTransforms,
      groupTransforms: ListBoxGroupTransforms,
    });
  }

  const state = useMultiComboBoxState({
    ...props,
    inputValue: props.value,
    defaultInputValue: props.defaultValue,
    getItems: collection.getItems,
    selection: props,
    trigger: props,
    expandable: props,
    allowsCustomValue: true,
    filter: {
      // Only default to `contains` if no `defaultFilter` was provided,
      // otherwise use whatever the user provided (which may be a falsy value)
      defaultFilter:
        props.defaultFilter === undefined ? contains : props.defaultFilter,
    },
  });

  return {collection, state, options: options || {}};
}

const InternalSearchField = createView(
  <T extends object>(allProps: View.RenderProps<SearchFieldProps<T>>) => {
    const uiConfig = useUiConfig();
    const {state, collection, options} = useSearchFieldState(allProps);
    const {shouldUsePopover = true} = options;
    const {
      data,
      item,
      group,
      children,
      label,
      placeholder,
      subviews,
      onSubmit,
      disabled,
      required,
      readOnly,
      emptyState,
      size,
      ...props
    } = allProps;

    const {
      searchIcon: searchFieldSubview,
      cancelAction: cancelActionSubview,
      textfield: textfieldSubview,
      ...fieldSubviews
    } = subviews;

    const isMobileAndTouch = uiConfig?.isTouch && uiConfig?.isSmallScreen;
    const ref = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const cancelActionRef = useRef<HTMLElement>(null);
    const {
      inputProps,
      listBoxProps,
      labelProps,
      descriptionProps,
      errorMessageProps,
    } = useComboBox(
      {
        ...props,
        shouldFocusWrap: true,
        allowsCustomValue: true,
        isDisabled: disabled,
        isReadOnly: readOnly,
        label,
        placeholder,
        inputRef,
        buttonRef: cancelActionRef,
        listBoxRef,
        popoverRef: shouldUsePopover ? popoverRef : listBoxRef,
      },
      state,
    );

    const [fieldProps] = useFormFieldProps({
      label,
      subviews: fieldSubviews,
      ...props,
      id: inputProps.id,
    });

    const onKeyDown = (e: React.KeyboardEvent) => {
      const key = e.key;

      if (key === 'Enter' || key === 'Escape') {
        e.preventDefault();
      }

      if (key === 'Enter') {
        onSubmit?.(
          state.inputValue,
          new Set(state.collection.getKeys() as string[]),
          state.selectionManager.focusedKey,
        );
      }

      if (key === 'Escape') {
        if (state.inputValue !== '') {
          e.stopPropagation();
        }
        state.setInputValue('');
      }
    };

    if (children) {
      throw new Error(
        'SearchField was provided children. This is not supported. You should provide items ' +
          'with the `data` prop',
      );
    }

    const listboxContents = (
      <FocusScope restoreFocus>
        <ListBox
          ref={listBoxRef}
          label={label}
          data={data}
          item={item}
          group={group}
          emptyState={emptyState}
          inherits={subviews.listbox as View.Subview<ListBoxProps<T>>}
          css={{
            height: 'fill',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            ...(isMobileAndTouch
              ? {}
              : {
                  // This should probably be viewport based, but this specific value
                  // is based on the existing select field implementation.
                  maxHeight: '480px',
                }),
          }}
        />
      </FocusScope>
    );

    const listbox = (
      <ListBoxContext.Provider
        value={{
          state,
          collection,
          options: {...listBoxProps, autoFocus: true},
        }}
      >
        {shouldUsePopover ? (
          // The boolean above isn't actually for opening it, just whether to use it
          <Popover
            targetRef={ref}
            ref={popoverRef}
            onClose={() => state.close()}
            dismissible={
              props.menuTrigger === 'focus'
                ? // Dismissal on outsidePress and outsideFocus is already handled
                  // by the combobox when trigger is 'focus', only listen for escapeKey.
                  DISMISS_ON_ESCAPE
                : undefined
            }
            placement="bottom start"
            shift={false}
            size={{
              maxHeight: 'fill',
              matchWidth: true,
            }}
            css={{
              keyline: 'transparent',
              // TODO: due to border/box-shadow differences between the trigger and the popover,
              // we have to nudge the size and position of the popover to align with the trigger
              bleedX: 'space.1',
              stack: 'y',
            }}
            inherits={subviews.popover}
          >
            {listboxContents}
          </Popover>
        ) : (
          listboxContents
        )}
      </ListBoxContext.Provider>
    );

    return (
      <CompositeFieldConfig.Customize
        subviews={{
          label: {uses: [assignProps(labelProps)]},
          description: {uses: [assignProps(descriptionProps)]},
          inputs: {
            css: {
              ':focus-within': {
                focusRing: state.selectionManager.focusedKey ? 'none' : 'focus',
              },
            },
            uses: [
              assignProps({ref}),
              disabled &&
                css({
                  backgroundColor: 'form.disabled',
                  pointerEvents: 'none',
                }),
            ],
          },
          error: {uses: [assignProps(errorMessageProps)]},
        }}
      >
        <CompositeField
          {...fieldProps}
          size={size}
          // use the old PopoverLayer until we refactor SearchField to be responsive
        >
          <Icon
            inherits={searchFieldSubview}
            icon={search}
            aria-hidden
            size="xsmall"
            key="search-icon"
          />
          <TextFieldConfig.Customize
            subviews={{
              input: css({
                '::-webkit-search-decoration': {
                  '-webkit-appearance': 'none',
                },
                '::-webkit-search-cancel-button': {
                  '-webkit-appearance': 'none',
                },
              }),
            }}
          >
            <TextField
              inherits={textfieldSubview}
              label={label}
              ref={inputRef}
              required={required}
              {...(inputProps as React.HTMLAttributes<HTMLInputElement>)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                onKeyDown(e);
                inputProps.onKeyDown?.(e);
              }}
              autoComplete="off"
              readOnly={readOnly}
              disabled={disabled}
              autoFocus={props.autoFocus}
              // value is always "controlled" on inputProps, so clear any default value
              defaultValue={undefined}
              type="search"
              subviews={{input: subviews.input}}
            />
          </TextFieldConfig.Customize>
          <Action
            inherits={cancelActionSubview}
            ref={cancelActionRef}
            tabIndex={-1}
            aria-hidden
            onPress={() => {
              state.setInputValue('');
            }}
            onPressStart={() => {
              // this is in PressStart for mobile so that touching the clear button doesn't remove focus from
              // the input and close the keyboard
              requestAnimationFrame(() => inputRef.current?.focus());
            }}
            disabled={disabled || readOnly}
            css={{
              visibility: state.inputValue ? 'visible' : 'hidden',
              display: 'inline',
              // TODO: shouldn't need stack or alignment here
              // but the visual regression tests show that the icon is not
              // vertically centered without it (despite looking OK in a browser)
              stack: 'z',
              alignY: 'center',
              alignSelfY: 'center',
            }}
          >
            <Icon icon={cancelCircleFilled} aria-hidden size="xsmall" />
          </Action>
        </CompositeField>
        <DeprecatedPopoverContext.Provider
          value={{
            useDeprecatedPopoverComponents: !!uiConfig?.disableStablePopover,
          }}
        >
          {state.isOpen ? listbox : null}
        </DeprecatedPopoverContext.Provider>
      </CompositeFieldConfig.Customize>
    );
  },
);

// NOTE: It is okay to use and export only the "multi" SearchField types and constants,
// as they include all properties from the current SearchField and only adds new optional properties.
export const SearchField = SearchFieldConfig.createGenericView(
  <T extends object>(props: View.RenderProps<MultiSearchFieldProps<T>>) => {
    const uiConfig = useUiConfig();
    const isMultiSelectionEnabled = !!uiConfig?.enableSailUiMultipleSelection;

    if (isMultiSelectionEnabled || props.selectionMode === 'multiple') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      return <MultiSearchField {...props} />;
    }

    // Note: if someone starts using the new props (since they are in the docs) and the feature flag is disabled,
    // it will be very confusing to see that they are not working.
    // old search field does not support the new props, so we are going to replace
    // them by the old props here in case the old ones are not defined, in the same way the new searchField is
    // working with the feature flag enabled

    const {selectedKeys, defaultSelectedKeys, onMultiSelectionChange, ...rest} =
      props;

    const oldProps = {
      ...(selectedKeys && [...selectedKeys].length > 0
        ? {selectedKey: [...selectedKeys][0]}
        : {}),
      ...(defaultSelectedKeys && [...defaultSelectedKeys].length > 0
        ? {defaultSelectedKey: [...defaultSelectedKeys][0]}
        : {}),
      ...(onMultiSelectionChange
        ? {
            onSelectionChange: (key: React.Key) =>
              onMultiSelectionChange(new Set([key])),
          }
        : {}),
      ...rest,
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return <InternalSearchField {...oldProps} />;
  },
);

export type {MultiSearchFieldOptions as SearchFieldOptions};

export {MultiSearchFieldContext as SearchFieldContext};
